import { css } from '@emotion/react';
import { FC, useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import BottomSheet from '../BottomSheet';
import Button from '../Button';
import { fontStyles, spacing, colors, buildPageSpacing } from '../../styles';
import { Portfolios } from '../../storage';

interface PortfolioSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (selectedPortfolioIds: string[]) => void | Promise<void>;
  title: string;
  symbol?: string;
  excludePortfolioId?: string | null;
  excludePortfolioIds?: string[];
}

export const PortfolioSelectionModal: FC<PortfolioSelectionModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  title,
  symbol,
  excludePortfolioId,
  excludePortfolioIds,
}) => {
  const storePortfolios = Portfolios.useState((s) => s.portfolios);
  // Filter out excluded portfolios (e.g., Plaid portfolios which can't be manually edited)
  const excludeSet = new Set([...(excludePortfolioIds || []), ...(excludePortfolioId ? [excludePortfolioId] : [])]);
  const displayPortfolios =
    excludeSet.size > 0 ? storePortfolios.filter((p) => !excludeSet.has(p.id)) : storePortfolios;
  const storeIsLoading = Portfolios.useState((s) => s.isLoading);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const lastSymbol = useRef<string | undefined>(undefined);

  // Initialize selected IDs when modal opens or symbol changes
  useEffect(() => {
    if (!isOpen) {
      setSelectedIds([]);
      lastSymbol.current = undefined;
      return;
    }

    // Only run if symbol changed or first open
    if (lastSymbol.current === symbol) return;
    lastSymbol.current = symbol;

    // Pre-select portfolios that already contain this symbol
    if (symbol && storePortfolios) {
      try {
        const preSelectedIds = storePortfolios
          .filter((p) => p.symbols && Array.isArray(p.symbols) && p.symbols.includes(symbol))
          .map((p) => p.id);
        setSelectedIds(preSelectedIds);
      } catch (error) {
        console.error('Error processing portfolios:', error);
        setSelectedIds([]);
      }
    } else {
      setSelectedIds([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, symbol]);

  const handleToggle = (id: string) => {
    // Create a new array to ensure React detects the state change
    const newSelectedIds = selectedIds.includes(id) ? selectedIds.filter((pid) => pid !== id) : [...selectedIds, id];

    // Update state with new array
    setSelectedIds(newSelectedIds);
  };

  const handleSubmit = async () => {
    try {
      await onSubmit(selectedIds);
      onClose();
    } catch (error) {
      console.error('Error submitting portfolio selection:', error);
      toast.error('Failed to update watchlists');
    }
  };

  const containerStyles = css`
    padding: ${spacing.deci} ${spacing.deci} ${buildPageSpacing(spacing.deci, 'BOTTOM')} ${spacing.deci};
  `;

  const listStyles = css`
    max-height: 50vh;
    overflow-y: auto;
    margin-bottom: ${spacing.deci};
  `;

  const rowStyles = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${spacing.deci} 0;
    cursor: pointer;
    border-bottom: 1px solid ${colors.cloud};

    &:last-child {
      border-bottom: none;
    }

    &:active {
      opacity: 0.7;
    }

    html[data-theme='dark'] & {
      border-bottom-color: ${colors.tungsten}50;
    }
  `;

  const labelStyles = css`
    ${fontStyles.dog};
    color: var(--text-color);
  `;

  const checkmarkStyles = css`
    color: ${colors.petrolBlue};
    font-size: 16px;

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
    }
  `;

  const buttonStyles = css`
    && {
      margin-top: ${spacing.deci};
      width: 100%;
      background-color: ${colors.petrolBlue};
      color: ${colors.snow};
      padding: ${spacing.deci} ${spacing.deci};
      border-radius: 8px;
      font-weight: 600;
      ${fontStyles.dog};
      text-align: center;
      display: flex;
      justify-content: center;
      align-items: center;

      &:disabled {
        opacity: 0.6;
        background-color: ${colors.silver};
      }
    }

    html[data-theme='dark'] && {
      background-color: ${colors.skyBlue};
      color: ${colors.liquorice};

      &:disabled {
        background-color: ${colors.silver}70;
        color: ${colors.tungsten};
      }
    }
  `;

  const removeAllStyles = css`
    ${fontStyles.mouse};
    color: ${colors.tungsten};
    text-align: center;
    padding: ${spacing.centi} 0;
    cursor: pointer;
    margin-top: ${spacing.centi};

    &:hover {
      text-decoration: underline;
    }

    html[data-theme='dark'] & {
      color: ${colors.silver};
    }
  `;

  const loadingStyles = css`
    ${fontStyles.cat};
    color: var(--secondary-text);
    text-align: center;
    padding: ${spacing.deci};
  `;

  const emptyStyles = css`
    ${fontStyles.cat};
    color: var(--secondary-text);
    text-align: center;
    padding: ${spacing.deci};
  `;

  const createWatchlistButtonStyles = css`
    display: block;
    width: 100%;
    background-color: transparent;
    color: ${colors.petrolBlue};
    border: 1px solid ${colors.petrolBlue};
    border-radius: 8px;
    padding: ${spacing.centi};
    ${fontStyles.dog};
    margin-top: ${spacing.centi};
    text-align: center;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      background-color: ${colors.petrolBlue}11;
    }

    html[data-theme='dark'] & {
      color: ${colors.skyBlue};
      border-color: ${colors.skyBlue};

      &:hover {
        background-color: ${colors.skyBlue}11;
      }
    }
  `;

  // Track if symbol was initially in any portfolio (for showing "Remove from all")
  const initiallyInPortfolios = useRef<boolean>(false);
  useEffect(() => {
    if (isOpen && symbol && storePortfolios) {
      initiallyInPortfolios.current = storePortfolios.some(
        (p) => p.symbols && Array.isArray(p.symbols) && p.symbols.includes(symbol),
      );
    }
  }, [isOpen, symbol, storePortfolios]);

  const handleRemoveAll = async () => {
    setSelectedIds([]);
    try {
      await onSubmit([]);
      onClose();
    } catch (error) {
      console.error('Error removing from all portfolios:', error);
      toast.error('Failed to update watchlists');
    }
  };

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title={title}>
      <div css={containerStyles}>
        {storeIsLoading ? (
          <div css={loadingStyles}>Loading portfolios...</div>
        ) : (
          <>
            {displayPortfolios.length === 0 ? (
              <div css={emptyStyles}>
                <p>No watchlists found.</p>
                <a href="/portfolios/add" css={createWatchlistButtonStyles}>
                  Create a Watchlist
                </a>
              </div>
            ) : (
              <>
                <div css={listStyles}>
                  {displayPortfolios.map((portfolio) => {
                    const isSelected = selectedIds.includes(portfolio.id);
                    return (
                      <div
                        key={portfolio.id}
                        css={rowStyles}
                        role="button"
                        aria-pressed={isSelected}
                        tabIndex={0}
                        onClick={() => handleToggle(portfolio.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleToggle(portfolio.id);
                          }
                        }}
                      >
                        <span css={labelStyles}>{portfolio.title}</span>
                        {isSelected && <span css={checkmarkStyles}>✓</span>}
                      </div>
                    );
                  })}
                </div>
                <Button onClick={handleSubmit} css={buttonStyles} block>
                  Save
                </Button>
                {initiallyInPortfolios.current && (
                  <div
                    css={removeAllStyles}
                    onClick={handleRemoveAll}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleRemoveAll();
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    Remove from all
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </BottomSheet>
  );
};

export default PortfolioSelectionModal;
